// @givmo/board — the annual meeting's election suggestion (CBG-YR3-ANNUAL, frame t4; the backend's
// contract `board-terms.md` §8).
//
// Plan §1.4's "elections auto-seeded onto the annual-meeting agenda", held to §3.6: a SUGGESTION the
// officer adds or puts away, never an item Board places. Only on a meeting typed annual, only for
// someone who can change its agenda (`board_meeting:write`), and only while there is something to
// suggest. The read is computed by the backend from the roster (nothing stored): directors whose
// current term's recorded end has passed or falls within three months after the meeting's day, and
// officers whose title's recorded end does — or, with the officers' term length recorded and only a
// start, the end it gives, which this card names with its source. Whether an end has passed is this
// reader's to say, against their own day.
//
//   * "Add elections to the agenda" (primary): the backend recomputes under the meeting's lock and
//     appends "Election of directors" / "Election of officers" — or, when the annual template's own
//     "Directors" / "Officers" sections are on the agenda, writes the names into them ("Add these
//     names to …"; those items keep their own origin). The meeting records the press, so the card
//     goes and a second press adds nothing.
//   * "Not for this meeting" (quiet): recorded on the meeting, reversibly — one quiet line, "Show the
//     terms ending", brings the card back.
// Every word was approved in copy round CBG-YR3-COPY1 (Jonathan, 2026-10-14 21:07 PDT), but the reused holdover line, approved before.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import { Button, Card, CardBody, toast } from '@givmo/design-system';
import { useBoardOperations } from '../../board-runtime';
import { boardCalendarKeys } from '../../calendar/calendar-queries';
import { displayDate, todayAsDateInput } from '../../lib/display';
import { joinWords, keptTogether } from '../roster/officer-roles';
import { officerRolesL3Words as l3 } from '../roster/officer-roles-l3-words';
import { extractTermSuggestions } from '../roster/terms-decode';
import { termsWords as tw } from '../roster/terms-words';

/** The annual template's own election sections, as its words name them (the backend's
 *  `ANNUAL_TEMPLATE_ELECTION_SECTIONS`). */
const TEMPLATE_SECTION_TITLES = { directors: 'Directors', officers: 'Officers' } as const;

function Line({ name, said, testId }: { name: string; said: string; testId: string }) {
  return (
    <li
      className="flex flex-wrap items-baseline justify-between gap-x-12 gap-y-2 border-t border-border-subtle py-8 first:border-t-0 max-sm:flex-col max-sm:items-start"
      data-testid={testId}
    >
      <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">{name}</span>
      <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
        {said}
      </span>
    </li>
  );
}

export function TermSuggestionCard({ orgRef, meetingId }: { orgRef: string; meetingId: string }) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const suggestionsOptions = operations.officerGetBoardTermSuggestionsOptions({
    path: { org_ref: orgRef, meeting_id: meetingId },
  });
  const query = useQuery({ ...suggestionsOptions, select: extractTermSuggestions });

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: suggestionsOptions.queryKey }),
      queryClient.invalidateQueries({
        queryKey: operations.officerListBoardAgendaItemsOptions({
          path: { org_ref: orgRef, meeting_id: meetingId },
        }).queryKey,
      }),
      // The press and the dismissal each add a line to the meeting's own history.
      queryClient.invalidateQueries({ queryKey: boardCalendarKeys(orgRef).events(meetingId) }),
    ]);

  const act = useMutation({
    mutationFn: async (kind: 'add' | 'dismiss' | 'restore') => {
      const options = {
        path: { org_ref: orgRef, meeting_id: meetingId },
        throwOnError: true as const,
      };
      if (kind === 'add') await operations.officerAddBoardTermSuggestionItems(options);
      else if (kind === 'dismiss') await operations.officerDismissBoardTermSuggestion(options);
      else await operations.officerRestoreBoardTermSuggestion(options);
    },
    onSuccess: (_data, kind) => {
      if (kind === 'add') toast.success(tw.electionsAdded);
      void refresh();
    },
    onError: (caught) => toast.error(isGivmoError(caught) ? caught.message : tw.suggestionFailed),
  });

  const read = query.data ?? null;
  if (read === null || !read.offered || read.addedAt !== null) return null;
  const { directors, officers } = read;
  if (directors.length === 0 && officers.length === 0) return null;

  // Put away for this meeting: one quiet line brings it back.
  if (read.dismissedAt !== null) {
    return (
      <Button
        appearance="saas"
        tone="link"
        size="sm"
        className="self-start"
        loading={act.isPending}
        onClick={() => act.mutate('restore')}
        testId="term-suggestion-restore"
      >
        {tw.showAgain}
      </Button>
    );
  }

  const today = todayAsDateInput();
  const said = (end: string) =>
    end < today
      ? keptTogether(l3.termEndedStillServing(displayDate(end)))
      : tw.endsOn(displayDate(end));
  // The template's own sections the names will go into, for the groups that have people.
  const sections: string[] = [];
  if (directors.length > 0 && read.templateSections.directors !== null)
    sections.push(TEMPLATE_SECTION_TITLES.directors);
  if (officers.length > 0 && read.templateSections.officers !== null)
    sections.push(TEMPLATE_SECTION_TITLES.officers);
  const groupsWithPeople = (directors.length > 0 ? 1 : 0) + (officers.length > 0 ? 1 : 0);
  const addLabel =
    sections.length > 0 && sections.length === groupsWithPeople
      ? tw.addNamesTo(joinWords(sections.map(tw.quoted)))
      : tw.addElections;

  return (
    <Card testId="term-suggestion-card">
      <CardBody className="flex flex-col gap-12">
        <div className="flex flex-col gap-4">
          <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
            {tw.suggestionTitle}
          </h2>
          <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {tw.suggestionLine}
          </p>
        </div>

        {directors.length > 0 ? (
          <div className="flex flex-col gap-6">
            <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
              {tw.directors}
            </span>
            <ul className="flex flex-col">
              {directors.map((row) => (
                <Line
                  key={row.board_member_id}
                  name={row.display_name}
                  said={
                    row.class_label
                      ? `${said(row.ends_on)} · ${row.class_label}`
                      : said(row.ends_on)
                  }
                  testId={`term-suggestion-director-${row.board_member_id}`}
                />
              ))}
            </ul>
          </div>
        ) : null}

        {officers.length > 0 ? (
          <div className="flex flex-col gap-6">
            <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
              {tw.officers}
            </span>
            <ul className="flex flex-col">
              {officers.map((row) => (
                <Line
                  key={`${row.board_member_id}:${row.ends_on}:${row.ends_on_from ?? ''}`}
                  name={`${row.display_name}, ${joinWords(row.title_labels ?? [])}`}
                  said={
                    row.ends_on_from === 'officer_term_length' && read.officerTerm !== null
                      ? `${said(row.ends_on)} · ${tw.fromOfficerTerm(read.officerTerm.years, read.officerTerm.citation)}`
                      : said(row.ends_on)
                  }
                  testId={`term-suggestion-officer-${row.board_member_id}`}
                />
              ))}
            </ul>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-10 pt-4">
          <Button
            appearance="saas"
            tone="primary"
            loading={act.isPending && act.variables === 'add'}
            disabled={act.isPending}
            onClick={() => act.mutate('add')}
            testId="term-suggestion-add"
          >
            {addLabel}
          </Button>
          <Button
            appearance="saas"
            tone="ghost"
            disabled={act.isPending}
            onClick={() => act.mutate('dismiss')}
            testId="term-suggestion-dismiss"
          >
            {tw.notForThisMeeting}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
